/** Public document acquisition contracts supported by the London reader.
 * Canonical application ownership stays separate from the native document host.
 * These checks validate returned links; the backend verifies source receipts,
 * document versions and stored PDF bytes before publishing them.
 */
const IDOX_HOSTS: Record<string,string> = {
  barnet:'publicaccess.barnet.gov.uk',bexley:'pa.bexley.gov.uk',brent:'pa.brent.gov.uk',
  'city-of-london':'www.planning2.cityoflondon.gov.uk',croydon:'publicaccess3.croydon.gov.uk',
  ealing:'pam.ealing.gov.uk',enfield:'planningandbuildingcontrol.enfield.gov.uk',
  greenwich:'planning.royalgreenwich.gov.uk','kingston-upon-thames':'publicaccess.kingston.gov.uk',
  lambeth:'planning.lambeth.gov.uk',lewisham:'planning.lewisham.gov.uk',newham:'pa.newham.gov.uk',
  sutton:'planningregister.sutton.gov.uk','tower-hamlets':'development.towerhamlets.gov.uk',
  westminster:'idoxpa.westminster.gov.uk',
};

function publicUrl(raw: unknown,allowQueryBackslash=false): URL | null {
  if (typeof raw!=='string' || raw!==raw.trim() || /[\x00-\x1f]/.test(raw) ||
      /[\\\s]/.test(raw.match(/^https:\/\/([^/?#]+)/)?.[1] || '') ||
      /\\/.test(raw.split('?')[0]) || (!allowQueryBackslash && /\\/.test(raw))) return null;
  try {
    const url=new URL(raw);
    return url.protocol==='https:' && !url.username && !url.password && !url.port && !url.hash ? url : null;
  } catch (_) { return null; }
}

function exactQuery(url: URL, expected: Record<string,string>) {
  const pairs=Array.from(url.searchParams.entries());
  return pairs.length===Object.keys(expected).length && pairs.every(([key,value])=>expected[key]===value) &&
    new Set(pairs.map(([key])=>key)).size===pairs.length;
}

function glaSource(url: URL) {
  return url.hostname==='planningdata.london.gov.uk' && !url.search &&
    /^\/api-guest\/applications\/_source\/[^/]+$/.test(url.pathname);
}

export function qualifiedLondonDocumentSource(row: Record<string,any>,doc: Record<string,any>,raw: unknown,cached: boolean) {
  const source=publicUrl(row.source_url),file=publicUrl(raw,row.source_council==='hillingdon' && row.source_system==='hillingdon-ocella');
  if (!source || !file || doc.source_system!==row.source_system) return false;
  const slug=row.source_council,system=row.source_system;
  const key=doc.source_document_id;
  if (system==='idox') {
    const host=IDOX_HOSTS[slug];
    return !!host && source.hostname===host && file.hostname===host && !file.search &&
      (cached ? /^\/online-applications\/files\/[0-9a-f]{32}\/pdf\/[^/]+\.pdf$/i
        : /^\/online-applications\/files\/[0-9a-f]{32}\/(?:pdf\/)?[^/]+$/i).test(file.pathname);
  }
  if ((slug==='haringey' && system==='haringey-arcus') || (slug==='bromley' && system==='bromley-arcus')) {
    const host=slug==='haringey' ? 'publicregister.haringey.gov.uk' : 'planningaccess.bromley.gov.uk';
    return source.hostname===host && /^\/pr\/s\/detail\/[A-Za-z0-9]{18}$/.test(source.pathname) &&
      exactQuery(source,{c__r:'Arcus_BE_Public_Register'}) && doc.source_document_provider===system &&
      typeof key==='string' && /^069[A-Za-z0-9]{15}$/.test(key) &&
      typeof doc.source_version_id==='string' && /^068[A-Za-z0-9]{15}$/.test(doc.source_version_id) &&
      file.href===`https://${host}/pr/sfc/servlet.shepherd/version/download/${doc.source_version_id}`;
  }
  if ((slug==='wandsworth' && system==='wandsworth-agile') || (slug==='redbridge' && system==='redbridge-agile')) {
    return source.hostname===`planning.${slug}.gov.uk` && !source.search &&
      new RegExp(`^/${slug}/application-details/[1-9]\\d{0,7}$`).test(source.pathname) &&
      typeof key==='string' && /^[A-Za-z0-9_-]{1,512}$/.test(key) &&
      file.href===`https://planningapi.agileapplications.co.uk/api/application/document/${slug.toUpperCase()}/${key}`;
  }
  if (slug==='camden' && ['camden-socrata','camden-planning-explorer'].includes(system)) {
    const native=system==='camden-socrata'
      ? source.hostname==='opendata.camden.gov.uk' && source.pathname==='/resource/2eiu-s2cw.json' &&
        /^\d+$/.test(source.searchParams.get('pk') || '') && Array.from(source.searchParams).length===1
      : source.hostname==='planningrecords.camden.gov.uk' && source.pathname==='/NECSWS/PlanningExplorer/Generic/StdDetails.aspx' &&
        /^[1-9]\d*$/.test(source.searchParams.get('PARAM0') || '');
    return native && doc.source_document_provider==='camden-webdrawer' && typeof key==='string' && /^[1-9]\d{0,19}$/.test(key) &&
      file.hostname==='camdocs.camden.gov.uk' && file.pathname===`/CMWebDrawer/Record/${key}/file/document` && exactQuery(file,{inline:''});
  }
  if (slug==='hillingdon' && system==='hillingdon-ocella') {
    const filename=doc.filename;
    return source.hostname==='planning.hillingdon.gov.uk' && source.pathname==='/OcellaWeb/planningDetails' &&
      exactQuery(source,{reference:row.reference_no,from:'planningSearch'}) && doc.source_document_provider==='hillingdon-ocella' &&
      typeof filename==='string' && filename.length>0 && filename.length<=1000 && !/[\\/:%\x00-\x1f]/.test(filename) && !['.','..'].includes(filename) &&
      key===`dv_pl_files\\${row.reference_no.replaceAll('/','_')}\\${filename}` &&
      file.hostname==='planning.hillingdon.gov.uk' && file.pathname==='/OcellaWeb/viewDocument' && exactQuery(file,{module:'pl',file:key});
  }
  if (system!=='london-datahub' || !glaSource(source)) return false;
  if (slug==='kensington-chelsea') {
    const ref=row.reference_no,handle=doc.source_download_handle;
    if (typeof ref!=='string' || !/^PP\/\d{2}\/\d{5}$/.test(ref) ||
        source.href!=='https://planningdata.london.gov.uk/api-guest/applications/_source/'+
          encodeURIComponent('Kensington_&_Chelsea-'+ref.replaceAll('/','_')) ||
        doc.native_document_provider!=='rbkc-publisher' || doc.identity_policy!=='exact_public_metadata_v1' ||
        typeof key!=='string' || !/^metadata:[0-9a-f]{64}$/.test(key) ||
        typeof handle!=='string' || !/^[A-F0-9]{32}$/.test(handle) ||
        doc.source_date_label!=='Date' || typeof doc.source_date_raw!=='string' ||
        typeof doc.doc_type!=='string' || !doc.doc_type.trim() || typeof doc.desc!=='string' || !doc.desc.trim() ||
        typeof doc.format!=='string' || !/^[a-z0-9]{2,8}$/.test(doc.format)) return false;
    const path=file.pathname.match(/^\/publisher\/docs\/([A-F0-9]{32})\/Document-\1\.([A-Za-z0-9]{2,8})$/);
    return file.hostname==='planningsearch.rbkc.gov.uk' && !file.search && !!path && path[1]===handle &&
      path[2].toLowerCase()===doc.format && doc.filename===file.pathname.split('/').at(-1);
  }
  if (slug==='havering') {
    const ref=row.reference_no;
    if (typeof ref!=='string' || !/^P\d{4}\.\d{2}$/.test(ref) ||
        source.href!==`https://planningdata.london.gov.uk/api-guest/applications/_source/Havering-${ref}` ||
        doc.native_document_provider!=='havering-msp' || doc.native_reference!==ref ||
        typeof key!=='string' || !/^[1-9]\d{0,15}$/.test(key) || file.hostname!=='msp.havering.gov.uk') return false;
    return doc.format==='pdf'
      ? file.pathname==='/civica/Resource/Civica/Handler.ashx/doc/pagestream' && exactQuery(file,{DocNo:key,pdf:'true'})
      : !cached && doc.format==='unknown' && file.pathname==='/my-requests/document-viewer' && exactQuery(file,{DocNo:key});
  }
  if (slug==='islington' || slug==='richmond-upon-thames') {
    const provider=slug==='islington' ? 'islington-agile' : 'richmond-agile';
    const prefix=slug==='islington' ? '//api/application/document/IS/' : '/api/application/document/RICHMONDUPONTHAMES/';
    return doc.native_document_provider===provider && /^[1-9]\d{0,7}$/.test(doc.native_application_id || '') &&
      typeof key==='string' && /^[A-Z0-9]{1,512}$/.test(key) &&
      file.href==='https://planningapi.agileapplications.co.uk'+prefix+key;
  }
  if (slug==='southwark') {
    const filename=doc.filename,ref=row.reference_no.replaceAll('/','_');
    if (doc.native_document_provider!=='southwark-idox' || typeof key!=='string' || !/^\d{1,20}$/.test(key) ||
        typeof filename!=='string' || !/^\d{2}_AP_\d{4}$/.test(ref) || !filename.startsWith(ref+'-') ||
        typeof doc.format!=='string' || !/^[a-z0-9]{2,8}$/.test(doc.format) ||
        !filename.toLowerCase().endsWith('-'+key+'.'+doc.format) ||
        filename.length<=ref.length+key.length+doc.format.length+3) return false;
    return file.hostname==='planning.southwark.gov.uk' && !file.search && !/%/.test(raw as string) &&
      /^\/online-applications\/files\/[0-9a-f]{32}\/(?:pdf\/)?[^/]+$/i.test(file.pathname) &&
      decodeURIComponent(file.pathname).split('/').at(-1)===filename;
  }
  return false;
}
